import { memo, ReactNode } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { breakpoints, useNavSize } from '@frontend/responsiveness';
import { shell } from '@frontend/shell-utils';
import { theme } from '@frontend/theme';
import { NakedUl, Text, useTooltip } from '@frontend/design-system';
import { TOP_BAR_HEIGHT } from '../../../constants';
import { dispatchTopBarModalClose } from '../events/modal-events';
import { useHandleShortlinks } from '../hooks/use-handle-shortlinks';

export const TOP_BAR_ID = 'weave-top-bar';

export const HeaderRoot = ({ children }: { children: ReactNode }) => {
  return (
    <header
      css={css`
        align-items: center;
        background: ${theme.colors.white};
        display: flex;
        justify-content: space-between;
        max-height: ${TOP_BAR_HEIGHT}px;
        min-height: ${TOP_BAR_HEIGHT}px;
      `}
      id={TOP_BAR_ID}
      onClick={dispatchTopBarModalClose}
    >
      {children}
    </header>
  );
};

export const SectionFlex = ({ children }: { children: ReactNode }) => {
  const { topBarRef } = useHandleShortlinks();

  return (
    <section
      ref={topBarRef}
      className='top-bar__section'
      css={css`
        align-items: center;
        color: ${theme.colors.white};
        display: flex;
        height: 100%;
        width: 100%;
        column-gap: ${theme.spacing(1)};
        @media (max-width: ${breakpoints.xsmall.max}px) {
          column-gap: 0;
          justify-content: space-between;
          & > * {
            flex-grow: 0;
            height: 100%;
          }
          & > .top-bar__ul {
            flex-grow: 2;
            justify-content: space-between;
          }
        }
      `}
    >
      {children}
    </section>
  );
};

export const DevBadgeWrapper = () => {
  const { tooltipProps, triggerProps, Tooltip } = useTooltip();
  const { t } = useTranslation();
  const isDevEnv = localStorage.getItem('backendEnv') === 'dev';

  if (!isDevEnv) return null;

  return (
    <li>
      <Text
        css={{
          alignItems: 'center',
          background: theme.colors.secondary.eggplant50,
          color: 'white',
          display: 'flex',
          fontWeight: theme.font.weight.bold,
          height: '100%',
          padding: theme.spacing(2),
        }}
        {...triggerProps}
      >
        DEV
      </Text>
      <Tooltip {...tooltipProps}>
        <Text color='white'>{t('Using DEV endpoints.')}</Text>
        <Text color='white'>
          {t('Use')}{' '}
          <Text as='code' style={{ color: theme.colors.primary20 }}>
            ctrl + shift + e
          </Text>{' '}
          {t('to open the endpoint switcher.')}
        </Text>
      </Tooltip>
    </li>
  );
};

type DesktopNavListProps = {
  children: ReactNode;
};

export const DesktopNavList = memo(({ children }: DesktopNavListProps) => {
  const hasShellThemeEnabled = shell?.isShell && shell?.featureAvailability?.has('shell-theme');
  const navSize = useNavSize();
  const isXsmall = navSize.isLte('xsmall');

  if (isXsmall) return null;

  return (
    <NakedUl
      className='top-bar__ul'
      css={css([
        { display: 'flex', height: '100%' },
        hasShellThemeEnabled &&
          !isXsmall &&
          css`
            margin-left: auto;
          `,
      ])}
    >
      {children}
    </NakedUl>
  );
});
DesktopNavList.displayName = 'DesktopNavList';
